// src/pages/Home.jsx
import React, { useEffect, useMemo, useState } from "react";

/* assets */
import aboutImg from "../assets/masjid-nouh.png";


/* ------------------------ small utils ------------------------ */
const parseDateSafe = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toDateOnly = (v) => {
  const d = parseDateSafe(v);
  if (!d) return null;
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const formatDateDisplay = (v) => {
  const d = parseDateSafe(v);
  if (!d) return "";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const sameDay = (a, b) => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

/* ---------------- Recurrence expansion + occurrence generation ---------------- */

const computeInstanceEndDate = (event, instanceStart) => {
  const origStart = parseDateSafe(event?.startDate ?? event?.start_date ?? event?.date ?? event?.start);
  const origEnd = parseDateSafe(event?.endDate ?? event?.end_date);
  const instStart = parseDateSafe(instanceStart);
  if (!instStart) return instanceStart;

  if (origStart && origEnd && origEnd.getTime() >= origStart.getTime()) {
    const durMs = origEnd.getTime() - origStart.getTime();
    return new Date(instStart.getTime() + durMs);
  }
  return instStart;
};

const expandEventsWithInstances = (events = [], opts = {}) => {
  const { includeParentRuleCarrier = false } = opts;
  const out = [];

  for (const ev of events || []) {
    const isRec = Number(ev?.isRecursive) === 1 || ev?.isRecursive === true;
    const instances = Array.isArray(ev?.instances) ? ev.instances : [];

    if (isRec && instances.length) {
      // instances
      for (const inst of instances) {
        const instDate = inst?.instanceDate ?? inst?.date ?? inst?.startDate;
        const instStart = parseDateSafe(instDate);
        if (!instStart) continue;

        const instEnd = computeInstanceEndDate(ev, instStart);

        out.push({
          ...ev,
          startDate: instStart,
          endDate: instEnd,
          __isInstance: true,
          __instanceDate: instStart,
          __parentEventId: ev?.id ?? ev?.eventId ?? ev?._id ?? null,
        });
      }

      // keep rule carrier
      if (includeParentRuleCarrier) {
        out.push({
          ...ev,
          __isInstance: false,
          __ruleCarrier: true,
          __instanceDate: null,
          __parentEventId: ev?.id ?? ev?.eventId ?? ev?._id ?? null,
        });
      }
    } else {
      out.push({
        ...ev,
        __isInstance: false,
        __ruleCarrier: false,
        __instanceDate: null,
        __parentEventId: null,
      });
    }
  }

  out.sort((a, b) => {
    const aD = parseDateSafe(a?.startDate ?? a?.start_date ?? a?.date ?? a?.start) ?? new Date(0);
    const bD = parseDateSafe(b?.startDate ?? b?.start_date ?? b?.date ?? b?.start) ?? new Date(0);
    return aD.getTime() - bD.getTime();
  });

  return out;
};

/* ---------------- Event date helpers ---------------- */

const fmtRangeSmart = (start, end) => {
  if (!start) return "—";
  if (!end || sameDay(start, end)) return formatDateDisplay(start);
  return `${formatDateDisplay(start)} - ${formatDateDisplay(end)}`;
};

/* ---------------- Announcements ---------------- */
const ANNOUNCEMENT_IMAGE_BASE = (
  process.env.REACT_APP_IMAGE_BASE_URL || "https://cdn.connectummah.com/myuploadfiles/"
).replace(/\/+$/, "");

const cleanAnnouncementImagePath = (value) => {
  const cleaned = String(value ?? "").trim().replace(/^"+|"+$/g, "");
  if (!cleaned || cleaned.toLowerCase() === "null") return "";
  return cleaned;
};

const resolveAnnouncementImage = (value) => {
  const cleaned = cleanAnnouncementImagePath(value);
  if (!cleaned) return "";
  if (/^https?:\/\//i.test(cleaned)) return cleaned;
  return `${ANNOUNCEMENT_IMAGE_BASE}/${cleaned.replace(/^\/+/, "")}`;
};

const isActiveAnnouncement = (announcement) => {
  if (announcement?.active == null) return true;
  if (typeof announcement.active === "boolean") return announcement.active;
  return Number(announcement.active) === 1;
};

const getAnnouncementSequence = (announcement, fallback = 0) => {
  const sequence = Number(announcement?.sequence);
  return Number.isFinite(sequence) ? sequence : fallback;
};

const normalizeAnnouncement = (announcement, index = 0) => {
  const description = String(announcement?.description ?? announcement?.message ?? "").trim();
  const titleSource = announcement?.title ?? announcement?.name ?? description;
  const title = String(titleSource || "Announcement").trim();

  return {
    id: announcement?.id ?? announcement?.announcementId ?? index,
    title,
    description,
    image: resolveAnnouncementImage(announcement?.imageLink ?? announcement?.image ?? announcement?.imageUrl),
    sequence: getAnnouncementSequence(announcement, index),
  };
};

const AnnouncementsSection = ({ announcements = [], loading = false }) => {
  const [announcementIndex, setAnnouncementIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const data = useMemo(() => {
    const list = Array.isArray(announcements) ? announcements : [];
    return list
      .filter(isActiveAnnouncement)
      .map(normalizeAnnouncement)
      .sort((a, b) => a.sequence - b.sequence);
  }, [announcements]);

  useEffect(() => {
    if (data.length === 0) return;
    if (announcementIndex >= data.length) setAnnouncementIndex(0);
  }, [announcementIndex, data]);

  useEffect(() => {
    if (loading || data.length < 2 || isHovering) return;
    const id = setInterval(() => {
      setAnnouncementIndex((previous) => (previous + 1) % data.length);
    }, 5000);
    return () => clearInterval(id);
  }, [data, isHovering, loading]);

  return (
    <section id="announcements" className="announcements py-16 bg-gray-50">
      <div className="w-full px-4">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold text-gray-800">Announcements</h2>
        </div>

        {loading && data.length === 0 && (
          <div className="h-[560px] animate-pulse rounded-lg bg-gray-100" />
        )}

        {!loading && data.length === 0 && <div className="text-center text-gray-600">No announcements to show.</div>}

        {data.length > 0 && (
          <div
            className="relative overflow-hidden rounded-lg"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {data.length > 1 ? (
              <>
                <button
                  className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white p-2 shadow hover:bg-gray-50"
                  onClick={() =>
                    setAnnouncementIndex(announcementIndex > 0 ? announcementIndex - 1 : data.length - 1)
                  }
                  aria-label="Previous Announcement"
                  type="button"
                >
                  <span className="text-2xl">‹</span>
                </button>
                <button
                  className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white p-2 shadow hover:bg-gray-50"
                  onClick={() => setAnnouncementIndex((announcementIndex + 1) % data.length)}
                  aria-label="Next Announcement"
                  type="button"
                >
                  <span className="text-2xl">›</span>
                </button>
              </>
            ) : null}

            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${announcementIndex * 100}%)` }}
            >
              {data.map((announcement, idx) => {
                const hasDistinctDescription =
                  announcement.description &&
                  announcement.description.trim() !== announcement.title.trim();

                return (
                  <article
                    key={`${announcement.id ?? "announcement"}-${idx}`}
                    className="flex min-h-[560px] w-full flex-shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:h-[560px] md:flex-row"
                  >
                    <div className="flex h-[420px] w-full flex-shrink-0 items-center justify-center bg-gray-100 md:h-full md:w-2/3">
                      {announcement.image ? (
                        <img
                          src={announcement.image}
                          alt={announcement.title}
                          className="h-full w-full object-contain"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <span className="text-gray-500">No Image</span>
                      )}
                    </div>

                    <div className="flex w-full flex-col justify-center p-6 md:h-full md:w-1/3">
                      <div className="mb-3">
                        <h3 className="break-words text-xl font-bold text-gray-800">
                          {announcement.title}
                        </h3>
                      </div>

                      {hasDistinctDescription ? (
                        <p className="max-h-72 overflow-y-auto whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-600">
                          {announcement.description}
                        </p>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

/* ---------------- Services card ---------------- */
const FALLBACK_SERVICE_IMG = "https://placehold.co/600x400?text=Service";
const normalizeService = (s, i = 0) => ({
  id: s?.id ?? s?.serviceId ?? i + 1,
  title: s?.title ?? s?.name ?? "Service",
  description: s?.description ?? s?.desc ?? "",
  image: s?.image ?? s?.imageUrl ?? s?.thumbnail ?? "",
  category: s?.category ?? s?.type ?? "General",
});
const ServicesSection = ({ services = [], setCurrentPage, loading = false }) => {
  const data = Array.isArray(services) ? services.map(normalizeService) : [];
  return (
    <section className="services py-16 bg-gray-50">
      <div className="w-full px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Services & Programs</h2>
        </div>
        {loading && data.length === 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow animate-pulse h-72" />
            ))}
          </div>
        )}
        {!loading && data.length === 0 && <div className="text-center text-gray-600">No services to show.</div>}
        {data.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.map((service, idx) => (
              <div
                key={`${service.id ?? "svc"}-${idx}`}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <div className="h-64 overflow-hidden bg-gray-100 md:h-72">
                  <a data-fancybox="services" href={service.image || FALLBACK_SERVICE_IMG} data-caption={service.title || "Service"} className="h-full w-full block">
                    <img
                      src={service.image || FALLBACK_SERVICE_IMG}
                      alt={service.title || "Service"}
                      className="h-full w-full object-contain"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => (e.currentTarget.src = FALLBACK_SERVICE_IMG)}
                    />
                  </a>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xl font-bold text-gray-800">{service.title}</h3>
                    <span className="text-xs rounded-full px-2 py-1 bg-gray-100 text-gray-600">
                      {service.category || "General"}
                    </span>
                  </div>
                  {service.description ? (
                    <p className="text-gray-600 mb-4">{service.description}</p>
                  ) : (
                    <p className="text-gray-500 italic mb-4">Details coming soon.</p>
                  )}
                  <button
                    onClick={() => setCurrentPage("services")}
                    className="bg-[#0f9e68] text-white px-6 py-2 rounded-lg hover:bg-[#005333] transition-colors font-semibold"
                  >
                    Read More
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};


/* -------------------- HOME PAGE -------------------- */
export default function HomePage({
  setCurrentPage,
  events = [],
  announcements = [],
  services = [],
  dataLoading,
}) {
  const [eventIndex, setEventIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const displayEvents = useMemo(
    () => expandEventsWithInstances(events || [], { includeParentRuleCarrier: false }),
    [events]
  );
  useEffect(() => {
    if (dataLoading) return;
    if (!displayEvents || displayEvents.length < 2) return;
    if (isHovering) return;
    const id = setInterval(() => setEventIndex((prev) => (prev + 1) % displayEvents.length), 3000);
    return () => clearInterval(id);
  }, [displayEvents, isHovering, dataLoading]);

  useEffect(() => {
    if (!displayEvents || displayEvents.length === 0) return;
    if (eventIndex >= displayEvents.length) setEventIndex(0);
  }, [displayEvents, eventIndex]);
  return (
    <>
      {/* Banner: Events + Prayer timings */}
      <section className="banner">
        <div className="w-full px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 items-start">
            {/* Events slider */}
            <div className="lg:col-span-9">
              <div>
                {dataLoading ? (
                  <div className="mt-3 flex h-[560px] items-center justify-center rounded-lg bg-gray-50">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#005333] mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading events...</p>
                    </div>
                  </div>
                ) : displayEvents.length === 0 ? (
                  <div className="mt-3 flex h-[560px] items-center justify-center rounded-lg bg-gray-50">
                    <p className="text-gray-600">No events available at the moment.</p>
                  </div>
                ) : (
                  <div className="relative">
                    <button
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white rounded-full shadow p-2 z-10 hover:bg-gray-50"
                      onClick={() =>
                        setEventIndex(eventIndex > 0 ? eventIndex - 1 : displayEvents.length - 1)
                      }
                      aria-label="Previous Event"
                      type="button"
                    >
                      <span className="text-2xl">‹</span>
                    </button>
                    <button
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white rounded-full shadow p-2 z-10 hover:bg-gray-50"
                      onClick={() => setEventIndex((eventIndex + 1) % displayEvents.length)}
                      aria-label="Next Event"
                      type="button"
                    >
                      <span className="text-2xl">›</span>
                    </button>

                    <div
                      className="overflow-hidden rounded-lg"
                      onMouseEnter={() => setIsHovering(true)}
                      onMouseLeave={() => setIsHovering(false)}
                    >
                      <div
                        className="flex transition-transform duration-500 ease-in-out"
                        style={{ transform: `translateX(-${eventIndex * 100}%)` }}
                      >
                        {displayEvents.map((event, index) => {
                          const startD = parseDateSafe(
                            event?.startDate ?? event?.start_date ?? event?.date ?? event?.start
                          );
                          const endD = parseDateSafe(event?.endDate ?? event?.end_date ?? event?.end);

                          const address = event?.location ? String(event.location).trim() : "";
                          const country = event?.country_Name ? String(event.country_Name).trim() : "";
                          const city = event?.city_Name ? String(event.city_Name).trim() : "";
                          const state = event?.state_Name ? String(event.state_Name).trim() : "";

                          const locationParts = [address, city, state, country].filter(Boolean);
                          const locationText = locationParts.join(", ");

                          const startTime = event?.startTime ? String(event.startTime).trim() : "";
                          const endTime = event?.endTime ? String(event.endTime).trim() : "";
                          const timeText = startTime && endTime ? `${startTime} - ${endTime}` : startTime || endTime || "";

                          return (
                            <div key={index} className="flex-shrink-0 w-full">
                              <div className="mt-3 flex h-[560px] flex-col gap-0 overflow-hidden rounded-lg border border-gray-200 bg-white md:flex-row">
                                {/* image */}
                                <div className="md:w-2/3 w-full flex-shrink-0 h-full">
                                  <div className="w-full h-full flex items-center justify-center bg-white">
                                    {event?.image ? (
                                      <img
                                        src={event.image}
                                        alt={event?.name || "Event"}
                                        className="w-full h-full object-contain bg-gray-100"
                                        onError={(e) => {
                                          e.currentTarget.style.display = "none";
                                          const fallback =
                                            e.currentTarget.parentNode.querySelector(".image-fallback");
                                          if (fallback) fallback.style.display = "flex";
                                        }}
                                      />
                                    ) : null}
                                    <div
                                      className="image-fallback bg-gray-100 w-full h-full flex items-center justify-center"
                                      style={{ display: event?.image ? "none" : "flex" }}
                                    >
                                      <span className="text-gray-500">No Image</span>
                                    </div>
                                  </div>
                                </div>

                                {/* details */}
                                <div className="md:w-1/3 w-full flex flex-col justify-center p-6 flex-shrink-0 h-full">
                                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                                    {event?.name || event?.title || "Event"}
                                  </h3>

                                  <div className="space-y-2 text-gray-600 mb-3">
                                    {/* Date range (smart: single date if same) */}
                                    {startD ? (
                                      <p className="flex items-center">
                                        <span className="text-[#005333] mr-2">📅</span>
                                        <span>{fmtRangeSmart(toDateOnly(startD), toDateOnly(endD) || toDateOnly(startD))}</span>
                                      </p>
                                    ) : null}

                                    {/* Time range */}
                                    {timeText ? (
                                      <p className="flex items-center">
                                        <span className="text-[#005333] mr-2">⏰</span>
                                        {timeText}
                                      </p>
                                    ) : null}

                                    {/* Location */}
                                    {locationText ? (
                                      <div className="flex items-start">
                                        <span className="text-[#005333] mr-2 mt-[2px]">📍</span>
                                        <span className="whitespace-pre-wrap break-words leading-relaxed">
                                          {locationText}
                                        </span>
                                      </div>
                                    ) : null}
                                  </div>

                                  {/* description */}
                                  {event?.description ? (
                                    <div className="border-top border-gray-100 pt-3 mt-3 overflow-y-auto" style={{ maxHeight: "180px" }}>
                                      <p className="text-gray-600 text-sm whitespace-pre-wrap">{event.description}</p>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Prayer timings widget from old NOUH website */}
            <div className="lg:col-span-3 mt-3 h-[560px] lg:min-w-[280px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
              <iframe
                title="Masjid Nouh Prayer Timings"
                src="https://timing.athanplus.com/masjid/widgets/embed?theme=2&masjid_id=JdGv87AP"
                type="text/html"
                width="100%"
                height="100%"
                frameBorder="0"
                scrolling="no"
                className="block h-full w-full"
              />
            </div>
            {/* /Prayer timings widget */}
          </div>
        </div>
      </section>

      <AnnouncementsSection announcements={announcements} loading={dataLoading} />

      {/* About */}
      <section id="about" className="about scroll-mt-40 py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-gray-200 h-80 rounded-lg shadow-lg overflow-hidden flex items-center justify-center">
              <img src={aboutImg} alt="Masjid Nouh" className="h-full w-full object-contain bg-white p-4" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About NOUH</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                The Nouh Islamic Centre of Canada- Masjid Nouh is committed to building a united community through faith, education, and outreach. Join us in growing together with purpose and compassion.
              </p>
              <blockquote className="border-l-4 border-[#0f9e68] pl-4 italic text-[#0f9e68] mb-6">
                Prophet Muhammad (S.A.W.W): "If you wish for Allah to multiply your wealth, then purify it (through zakat)."
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      <ServicesSection services={services} setCurrentPage={setCurrentPage} loading={dataLoading} />
      
    </>
  );
}

/* ---------------- helpers for schedule mapping (unchanged behavior) ---------------- */
